import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Container, Typography, Card, CardContent, Grid, CircularProgress,
  Chip, Avatar, Divider, Tooltip, IconButton, Button, Tab, Tabs, Paper,
  TextField, Select, MenuItem, InputAdornment, FormControl, InputLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Stack,
} from '@mui/material';
import {
  Today as TodayIcon,
  History as HistoryIcon,
  Assignment as AssignIcon,
  PersonOff as AbsentIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Warning as WarnIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  Star as StarIcon,
  SwapHoriz as SwapIcon,
  EventNote as DutyIcon,
  CalendarMonth as CalIcon,
  AccessTime as TimeIcon,
  School as ClassIcon,
  Person as PersonIcon,
  LocationOn as LocationOnIcon,
  PersonOutline as PersonOutlineIcon,
  SwitchAccount as SwitchAccountIcon,
  HourglassEmpty as HourglassIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';

// ─────────────────────────────────────────────────────────────────────────────
// Theme tokens
// ─────────────────────────────────────────────────────────────────────────────
const T = {
  ink:      '#0F1923',
  slate:    '#1E2D3D',
  indigo:   '#2563EB',
  indigoD:  '#1D4ED8',
  teal:     '#0D9488',
  emerald:  '#059669',
  amber:    '#D97706',
  rose:     '#E11D48',
  violet:   '#7C3AED',
  sky:      '#0284C7',
  skyL:     '#E0F2FE',
  border:   '#E2E8F0',
  surface:  '#F0F4FA',
  card:     '#FFFFFF',
  muted:    '#64748B',
  faint:    '#94A3B8',
  ghost:    '#F8FAFC',
};
const ROW_OPTIONS = [5, 10, 15, 25];

const BASE  = process.env.REACT_APP_BASE_URL;
const tok   = () => sessionStorage.getItem('token');
const authH = () => ({ Authorization: tok() });
const postH = () => ({ 'Content-Type': 'application/json', Authorization: tok() });
const safeJ = async (r) => { try { return await r.json(); } catch { return null; } };

const apiGet  = (path) => fetch(`${BASE}${path}`, { headers: authH() });
const apiPost = (path, body) => fetch(`${BASE}${path}`, {
  method: 'POST', headers: postH(), body: JSON.stringify(body),
});

const fmt      = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const todayStr = () => new Date().toISOString().split('T')[0];

// ─────────────────────────────────────────────────────────────────────────────
// Shared atoms
// ─────────────────────────────────────────────────────────────────────────────
const TH = ({ children, align = 'left', width }) => (
  <TableCell align={align} sx={{
    bgcolor: '#EEF2F9',
    fontWeight: 700,
    fontSize: '0.72rem',
    color: T.slate,
    py: 1.4,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    borderBottom: `2px solid ${T.border}`,
    ...(width ? { width } : {}),
  }}>
    {children}
  </TableCell>
);

const StatusPill = ({ status }) => {
  const map = {
    Confirmed:  { bg: '#D1FAE5', color: T.emerald, dot: '#10B981' },
    Cancelled:  { bg: '#FFE4E6', color: T.rose,    dot: '#F43F5E' },
    Pending:    { bg: '#FEF3C7', color: T.amber,   dot: '#F59E0B' },
    Preferred:  { bg: '#EDE9FE', color: T.violet,  dot: '#8B5CF6' },
    Available:  { bg: '#DBEAFE', color: T.indigo,  dot: '#3B82F6' },
  };
  const s = map[status] || { bg: '#F1F5F9', color: T.muted, dot: T.faint };
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5,
      bgcolor: s.bg, color: s.color, fontWeight: 700, fontSize: '0.68rem',
      px: 1, py: 0.35, borderRadius: '20px', lineHeight: 1.5, whiteSpace: 'nowrap' }}>
      <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: s.dot, flexShrink: 0 }} />
      {status || '—'}
    </Box>
  );
};

const SearchBar = ({ value, onChange, placeholder = 'Search…', width = 260 }) => (
  <TextField size="small" placeholder={placeholder} value={value}
    onChange={e => onChange(e.target.value)}
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          <SearchIcon sx={{ fontSize: 15, color: T.faint }} />
        </InputAdornment>
      ),
    }}
    sx={{
      width,
      '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        fontSize: '0.83rem',
        bgcolor: T.ghost,
        '&:hover fieldset': { borderColor: T.indigo },
        '&.Mui-focused fieldset': { borderColor: T.indigo, borderWidth: 1.5 },
      },
    }}
  />
);

const Pager = ({ total, page, rowsPerPage, onPage, onRows }) => {
  const pages = Math.max(Math.ceil(total / rowsPerPage), 1);
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      px: 2.5, py: 1.5, borderTop: `1px solid ${T.border}`, flexWrap: 'wrap', gap: 1,
      bgcolor: T.ghost,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.73rem' }}>Rows per page:</Typography>
        <Select value={rowsPerPage} onChange={e => { onRows(Number(e.target.value)); onPage(1); }}
          size="small"
          sx={{ fontSize: '0.78rem', height: 28, minWidth: 60, borderRadius: '8px', bgcolor: T.card }}>
          {ROW_OPTIONS.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
        </Select>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.73rem' }}>
          {total === 0 ? '0' : `${Math.min((page - 1) * rowsPerPage + 1, total)}–${Math.min(page * rowsPerPage, total)}`} of {total}
        </Typography>
        <IconButton size="small" onClick={() => onPage(p => p - 1)} disabled={page <= 1}
          sx={{ '&:not(:disabled)': { color: T.indigo } }}>
          <PrevIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={() => onPage(p => p + 1)} disabled={page >= pages}
          sx={{ '&:not(:disabled)': { color: T.indigo } }}>
          <NextIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Teacher Status Badge — compact inline badge for vacancy card
// ─────────────────────────────────────────────────────────────────────────────
const TeacherStatusBadge = ({ isUnavailable }) => {
  if (isUnavailable) {
    return (
      <Box sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.5,
        bgcolor: '#FFF1F2', color: T.rose,
        fontWeight: 700, fontSize: '0.63rem',
        px: 0.9, py: 0.3, borderRadius: '6px',
        border: `1px solid #FECDD3`,
        whiteSpace: 'nowrap',
      }}>
        <AbsentIcon sx={{ fontSize: 10 }} />
        Absent
      </Box>
    );
  }
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5,
      bgcolor: T.skyL, color: T.sky,
      fontWeight: 700, fontSize: '0.63rem',
      px: 0.9, py: 0.3, borderRadius: '6px',
      border: `1px solid #BAE6FD`,
      whiteSpace: 'nowrap',
    }}>
      <SwitchAccountIcon sx={{ fontSize: 10 }} />
      Rescheduled
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// VacancyCard — updated to handle isUnavailable
// ─────────────────────────────────────────────────────────────────────────────
const VacancyCard = ({ slot, onAssign }) => {
  const covered      = slot.proxyAssigned  ?? slot.ProxyAssigned  ?? false;
  const isUnavailable = slot.isUnavailable ?? slot.isUnavailable  ?? true; // default to absent if not provided
  const subject      = slot.subjectName    ?? slot.SubjectName    ?? '—';
  const cls          = slot.className      ?? slot.ClassName      ?? '';
  const sec          = slot.sectionName    ?? slot.SectionName    ?? '';
  const period       = slot.periodName     ?? slot.PeriodName     ?? '';
  const start        = slot.startTime      ?? slot.StartTime      ?? '';
  const end          = slot.endTime        ?? slot.EndTime        ?? '';
  const teacher      = slot.teacherName    ?? slot.TeacherName    ?? 'Unknown';

  // Color scheme depends on coverage state
  const scheme = covered
    ? { border: '#A7F3D0', bg: '#F0FDF4', shadow: 'rgba(5,150,105,0.08)', hoverShadow: 'rgba(5,150,105,0.15)' }
    : isUnavailable
      ? { border: '#FCD34D', bg: '#FFFBEB', shadow: 'rgba(217,119,6,0.10)', hoverShadow: 'rgba(217,119,6,0.18)' }
      : { border: '#BAE6FD', bg: '#F0F9FF', shadow: 'rgba(2,132,199,0.08)', hoverShadow: 'rgba(2,132,199,0.16)' };

  return (
    <Card sx={{
      borderRadius: '14px',
      border: `1.5px solid ${scheme.border}`,
      bgcolor: scheme.bg,
      boxShadow: `0 2px 12px ${scheme.shadow}`,
      transition: 'all 0.2s',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 8px 24px ${scheme.hoverShadow}`,
      },
    }}>
      <CardContent sx={{ p: '16px !important' }}>

        {/* Header row — subject + coverage badge */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.2 }}>
          <Box sx={{ flex: 1, pr: 1 }}>
            <Typography sx={{ fontWeight: 800, color: T.ink, fontSize: '0.95rem', lineHeight: 1.25 }}>
              {subject}
            </Typography>
            <Typography variant="caption" sx={{ color: T.muted, fontWeight: 600 }}>
              {cls}{sec ? ` — ${sec}` : ''}
            </Typography>
          </Box>

          {/* Coverage status badge */}
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 0.4,
            bgcolor: covered ? '#D1FAE5' : isUnavailable ? '#FEF3C7' : T.skyL,
            color: covered ? T.emerald : isUnavailable ? T.amber : T.sky,
            fontWeight: 700, fontSize: '0.67rem',
            px: 1, py: 0.35, borderRadius: '20px',
            border: `1px solid ${covered ? '#6EE7B7' : isUnavailable ? '#FCD34D' : '#7DD3FC'}`,
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}>
            {covered
              ? <><CheckIcon sx={{ fontSize: 11 }} /> Covered</>
              : isUnavailable
                ? <><WarnIcon sx={{ fontSize: 11 }} /> Open</>
                : <><SwitchAccountIcon sx={{ fontSize: 11 }} /> Open</>
            }
          </Box>
        </Box>

        <Divider sx={{
          my: 1,
          borderColor: covered ? '#BBF7D0' : isUnavailable ? '#FDE68A' : '#BAE6FD',
          opacity: 0.7,
        }} />

        {/* Period & time */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.7 }}>
          <TimeIcon sx={{ fontSize: 12, color: T.faint }} />
          <Typography variant="caption" sx={{ color: T.muted, fontSize: '0.73rem' }}>
            Period {period}{start ? ` · ${start}–${end}` : ''}
          </Typography>
        </Box>

        {/* Teacher row — only show if isUnavailable, with status badge */}
        {isUnavailable ? (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 0.6,
            mb: covered ? 0 : 1.5,
          }}>
            <AbsentIcon sx={{ fontSize: 13, color: T.rose, flexShrink: 0 }} />
            <Typography variant="caption" sx={{ color: T.rose, fontWeight: 700, fontSize: '0.72rem' }}>
              {teacher}
            </Typography>
            <Typography variant="caption" sx={{ color: T.faint, fontSize: '0.7rem' }}>absent</Typography>
          </Box>
        ) : (
          /* Present teacher — rescheduled / needs cover for another slot */
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 0.6,
            mb: covered ? 0 : 1.5,
            p: '6px 8px',
            borderRadius: '8px',
            bgcolor: T.skyL,
            border: `1px solid #BAE6FD`,
          }}>
            <PersonOutlineIcon sx={{ fontSize: 13, color: T.sky, flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" sx={{ color: T.sky, fontWeight: 700, fontSize: '0.72rem', display: 'block', lineHeight: 1.3 }}>
                {teacher}
              </Typography>
              <Typography variant="caption" sx={{ color: T.sky, fontSize: '0.65rem', opacity: 0.8 }}>
                Present · needs cover for another slot
              </Typography>
            </Box>
          </Box>
        )}

        {/* Assign button — always shown when not covered */}
        {!covered && (
          <Button
            size="small"
            variant="contained"
            startIcon={<SwapIcon sx={{ fontSize: 13 }} />}
            onClick={() => onAssign(slot)}
            fullWidth
            sx={{
              bgcolor: isUnavailable ? T.indigo : T.sky,
              fontWeight: 700,
              fontSize: '0.75rem',
              borderRadius: '8px',
              textTransform: 'none',
              py: 0.7,
              boxShadow: isUnavailable
                ? '0 2px 8px rgba(37,99,235,0.3)'
                : '0 2px 8px rgba(2,132,199,0.25)',
              '&:hover': {
                bgcolor: isUnavailable ? T.indigoD : '#0369A1',
                boxShadow: isUnavailable
                  ? '0 4px 12px rgba(37,99,235,0.4)'
                  : '0 4px 12px rgba(2,132,199,0.35)',
              },
            }}
          >
            Assign Proxy
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// TeacherSuggRow
// ─────────────────────────────────────────────────────────────────────────────
function TeacherSuggRow({ teacher, selected, onSelect }) {
  const name   = teacher.teacherName  ?? teacher.TeacherName  ?? '—';
  const desig  = teacher.designation  ?? teacher.Designation  ?? '';
  const pref   = (teacher.availabilityTag ?? teacher.AvailabilityTag) === 'Preferred';
  const match  = teacher.isSubjectMatch ?? teacher.IsSubjectMatch ?? 0;

  return (
    <Box
      onClick={onSelect}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: '11px 14px',
        borderRadius: '10px',
        cursor: 'pointer',
        border: `1.5px solid ${selected ? T.indigo : T.border}`,
        bgcolor: selected ? '#EFF6FF' : pref ? '#FAF5FF' : T.card,
        transition: 'all 0.15s ease',
        boxShadow: selected ? `0 0 0 3px rgba(37,99,235,0.12)` : 'none',
        '&:hover': {
          borderColor: T.indigo,
          bgcolor: '#EFF6FF',
          boxShadow: `0 0 0 3px rgba(37,99,235,0.08)`,
        },
      }}
    >
      <Avatar sx={{
        width: 36, height: 36,
        fontWeight: 800,
        fontSize: '0.85rem',
        bgcolor: pref ? '#EDE9FE' : '#DBEAFE',
        color: pref ? T.violet : T.indigo,
        border: `1.5px solid ${pref ? '#C4B5FD' : '#BFDBFE'}`,
      }}>
        {name.charAt(0).toUpperCase()}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: T.ink, fontSize: '0.82rem' }}>
            {name}
          </Typography>
          {match === 1 && (
            <Tooltip title="Subject match">
              <StarIcon sx={{ fontSize: 12, color: T.violet }} />
            </Tooltip>
          )}
        </Box>
        {desig && (
          <Typography variant="caption" sx={{ color: T.muted, fontSize: '0.67rem' }}>
            {desig}
          </Typography>
        )}
      </Box>

      <StatusPill status={pref ? 'Preferred' : 'Available'} />

      <Box sx={{
        width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
        border: `2px solid ${selected ? T.indigo : T.border}`,
        bgcolor: selected ? T.indigo : 'transparent',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all 0.15s',
      }}>
        {selected && <CheckIcon sx={{ fontSize: 11, color: '#fff' }} />}
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────
export default function ProxyManagement() {
  const [tab, setTab] = useState(0);
const [vacFilter, setVacFilter] = useState('all');
  // ── Branch & Teacher dropdowns ───────────────────────────────────────────
  const [isMaster,          setIsMaster]          = useState(false);
  const [branches,          setBranches]          = useState([]);
  const [selectedBranchId,  setSelectedBranchId]  = useState(null);
  const [teachers,          setTeachers]          = useState([]);
  const [selectedTeacherId, setSelectedTeacherId] = useState('');
  const [loadingDropdowns,  setLoadingDropdowns]  = useState(false);
  const [suggSearch, setSuggSearch] = useState('');

  // Tab 0: Vacancies
  const [vacDate,    setVacDate]    = useState(todayStr());
  const [vacancies,  setVacancies]  = useState([]);
  const [loadingVac, setLoadingVac] = useState(false);

  // Assign dialog
  const [assignOpen,    setAssignOpen]    = useState(false);
  const [assignSlot,    setAssignSlot]    = useState(null);
  const [suggestions,   setSuggestions]   = useState([]);
  const [loadingSugg,   setLoadingSugg]   = useState(false);
  const [pickedId,      setPickedId]      = useState(null);
  const [pickedTeacher, setPickedTeacher] = useState(null);
  const [proxyDate,     setProxyDate]     = useState(todayStr());
  const [remarks,       setRemarks]       = useState('');
  const [savingProxy,   setSavingProxy]   = useState(false);

  // Tab 1: History
  const [history,     setHistory]     = useState([]);
  const [loadingHist, setLoadingHist] = useState(false);
  const [histFrom,    setHistFrom]    = useState('');
  const [histTo,      setHistTo]      = useState('');
  const [histSearch,  setHistSearch]  = useState('');
  const [histPage,    setHistPage]    = useState(1);
  const [histRows,    setHistRows]    = useState(10);

  // Cancel dialog
  const [cancelOpen,   setCancelOpen]   = useState(false);
  const [cancelRec,    setCancelRec]    = useState(null);
  const [cancelRemark, setCancelRemark] = useState('');
  const [cancelling,   setCancelling]   = useState(false);

  // Tab 2: My Duties
  const [duties,      setDuties]      = useState([]);
  const [loadingDuty, setLoadingDuty] = useState(false);
  const [dutyFrom,    setDutyFrom]    = useState(todayStr());
  const [dutySearch,  setDutySearch]  = useState('');
  const [dutyPage,    setDutyPage]    = useState(1);
  const [dutyRows,    setDutyRows]    = useState(10);

  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const showSnack = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  // ── Init ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    const masterFlag = sessionStorage.getItem('is_Master') === 'true';
    setIsMaster(masterFlag);
    if (masterFlag) {
      fetchBranches();
    } else {
      const branchId = sessionStorage.getItem('branchId');
      if (branchId) setSelectedBranchId(Number(branchId));
    }
  }, []);

  useEffect(() => {
    if (selectedBranchId) fetchTeachers(selectedBranchId);
  }, [selectedBranchId]);

  const filteredVacancies = useMemo(() => {
  if (vacFilter === 'absent')      return vacancies.filter(s => !( s.proxyAssigned ?? s.ProxyAssigned) &&  (s.isUnavailable ?? s.IsUnavailable));
  if (vacFilter === 'rescheduled') return vacancies.filter(s => !( s.proxyAssigned ?? s.ProxyAssigned) && !(s.isUnavailable ?? s.IsUnavailable));
  if (vacFilter === 'covered')     return vacancies.filter(s =>    s.proxyAssigned ?? s.ProxyAssigned);
  return vacancies;
}, [vacancies, vacFilter]);

  // ── Fetch Branches ───────────────────────────────────────────────────────
  const fetchBranches = async () => {
    setLoadingDropdowns(true);
    try {
      const res  = await fetch(`${BASE}/Branch/GetBranch`, { method: 'POST', headers: postH(), body: JSON.stringify({}) });
      const data = await safeJ(res);
      if (Array.isArray(data) && data.length > 0) {
        setBranches(data);
        setSelectedBranchId(data[0].branchId);
      }
    } catch (e) {
      console.error('Failed to fetch branches', e);
      showSnack('Failed to load branches', 'error');
    } finally {
      setLoadingDropdowns(false);
    }
  };

  // ── Fetch Teachers ───────────────────────────────────────────────────────
  const fetchTeachers = async (branchId) => {
    try {
      const res  = await apiPost(`/AssignmentCreate/GetTeacher`, {});
      const data = await safeJ(res);
      const arr  = Array.isArray(data) ? data : [];
      setTeachers(arr);
      setSelectedTeacherId('');
    } catch (e) {
      console.error('Failed to fetch teachers', e);
      showSnack('Failed to load teachers', 'error');
    }
  };

  // ── Loaders ──────────────────────────────────────────────────────────────
  const loadVacancies = useCallback(async () => {
    if (!selectedBranchId) return;
    setLoadingVac(true);
    try {
      const params = new URLSearchParams();
      if (vacDate) params.set('proxyDate', vacDate);
      params.set('Branch_id', selectedBranchId);
      const res = await apiGet(`/adv/proxy/today-vacancies?${params}`);
      const d   = await safeJ(res);
      setVacancies(Array.isArray(d) ? d : []);
    } catch (e) {
      console.error(e);
      showSnack('Failed to load vacancies', 'error');
    } finally {
      setLoadingVac(false);
    }
  }, [vacDate, selectedBranchId]);

  const loadHistory = useCallback(async () => {
    if (!selectedBranchId) return;
    setLoadingHist(true);
    try {
      const params = new URLSearchParams();
      if (histFrom) params.set('fromDate', histFrom);
      if (histTo)   params.set('toDate',   histTo);
      params.set('Branch_id', selectedBranchId);
      const res = await apiGet(`/adv/proxy/history?${params}`);
      const d   = await safeJ(res);
      setHistory(Array.isArray(d) ? d : []);
    } catch (e) {
      console.error(e);
      showSnack('Failed to load history', 'error');
    } finally {
      setLoadingHist(false);
    }
  }, [histFrom, histTo, selectedBranchId]);

  const loadDuties = useCallback(async () => {
    if (!selectedBranchId) return;
    setLoadingDuty(true);
    try {
      const params = new URLSearchParams();
      if (dutyFrom)           params.set('fromDate',       dutyFrom);
      if (selectedTeacherId)  params.set('ProxyTeacherId', selectedTeacherId);
      params.set('Branch_id', selectedBranchId);
      const res = await apiGet(`/adv/proxy/my-duties?${params}`);
      const d   = await safeJ(res);
      setDuties(Array.isArray(d) ? d : []);
    } catch (e) {
      console.error(e);
      showSnack('Failed to load duties', 'error');
    } finally {
      setLoadingDuty(false);
    }
  }, [dutyFrom, selectedTeacherId, selectedBranchId]);

  useEffect(() => { if (selectedBranchId) loadVacancies(); }, [vacDate, selectedBranchId]);
  useEffect(() => { if (tab === 1 && selectedBranchId) loadHistory(); }, [tab, histFrom, histTo, selectedBranchId]);
  useEffect(() => { if (tab === 2 && selectedBranchId) loadDuties(); }, [tab, dutyFrom, selectedTeacherId, selectedBranchId]);

  // ── Suggestions ──────────────────────────────────────────────────────────
  const loadSuggestions = async (slot, date) => {
    setLoadingSugg(true);
    setSuggestions([]);
    setPickedId(null);
    setPickedTeacher(null);
    try {
      const body = {
        ClassId:         slot.classId         ?? slot.ClassId         ?? 0,
        SectionId:       slot.sectionId       ?? slot.SectionId       ?? 0,
        SubjectId:       slot.subjectId       ?? slot.SubjectId       ?? 0,
        PeriodId:        slot.periodId        ?? slot.PeriodId        ?? 0,
        DayId:           slot.dayId           ?? slot.DayId           ?? 0,
        AbsentTeacherId: slot.teacherId       ?? slot.TeacherId       ?? 0,
        ProxyDate:       date,
        Branch_id:       selectedBranchId     ?? 0,
      };
      const res = await apiPost(`/adv/proxy/suggestions`, body);
      const d   = await safeJ(res);
      setSuggestions(Array.isArray(d) ? d : []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingSugg(false);
    }
  };

  const handleOpenAssign = (slot) => {
    setAssignSlot(slot);
    setPickedId(null);
    setPickedTeacher(null);
    setRemarks('');
    setRemarks('');
setSuggSearch('');
    setProxyDate(vacDate || todayStr());
    setAssignOpen(true);
    loadSuggestions(slot, vacDate || todayStr());
  };

  useEffect(() => {
    if (assignOpen && assignSlot) loadSuggestions(assignSlot, proxyDate);
  }, [proxyDate]); // eslint-disable-line

  const handleSelectTeacher = (t) => {
    const id = t.teacherId ?? t.TeacherId;
    setPickedId(id);
    setPickedTeacher(t);
  };

  // ── Save proxy ────────────────────────────────────────────────────────────
  const handleSaveProxy = async () => {
    if (!pickedTeacher || !assignSlot) return;
    setSavingProxy(true);
    try {
      const body = {
        TimetableId:     assignSlot.timetableId     ?? assignSlot.TimetableId     ?? 0,
        ProxyDate:       proxyDate,
        AbsentTeacherId: assignSlot.teacherId        ?? assignSlot.TeacherId        ?? 0,
        ClassId:         assignSlot.classId          ?? assignSlot.ClassId          ?? 0,
        SectionId:       assignSlot.sectionId        ?? assignSlot.SectionId        ?? 0,
        SubjectId:       assignSlot.subjectId        ?? assignSlot.SubjectId        ?? 0,
        PeriodId:        assignSlot.periodId         ?? assignSlot.PeriodId         ?? 0,
        DayId:           assignSlot.dayId            ?? assignSlot.DayId            ?? 0,
        ProxyTeacherId:  pickedTeacher.teacherId      ?? pickedTeacher.TeacherId     ?? 0,
        Remarks:         remarks,
        Branch_id:       selectedBranchId            ?? 0,
      };
      const res = await apiPost(`/adv/proxy/save`, body);
      const d   = await safeJ(res);
      const st  = d?.status ?? d?.Status ?? '';
      const msg = d?.msg    ?? d?.Msg    ?? '';
      if (st === 'Succeed!' || res.ok) {
        showSnack(msg || 'Proxy assigned successfully!');
        setAssignOpen(false);
        loadVacancies();
        if (tab === 1) loadHistory();
      } else {
        showSnack(msg || 'Assignment failed. Please try again.', 'error');
      }
    } catch {
      showSnack('Error assigning proxy', 'error');
    } finally {
      setSavingProxy(false);
    }
  };

  // ── Cancel proxy ──────────────────────────────────────────────────────────
  const handleCancelProxy = async () => {
    if (!cancelRec) return;
    setCancelling(true);
    try {
      const res = await apiPost(`/adv/proxy/cancel`, {
        Id:       cancelRec.id ?? cancelRec.Id,
        Remarks:  cancelRemark,
        Branch_id: selectedBranchId ?? 0,
      });
      const d   = await safeJ(res);
      const st  = d?.status ?? d?.Status ?? '';
      const msg = d?.msg    ?? d?.Msg    ?? '';
      if (st === 'Succeed!' || res.ok) {
        showSnack(msg || 'Proxy cancelled');
        setCancelOpen(false);
        setCancelRemark('');
        loadHistory();
      } else {
        showSnack(msg || 'Cancellation failed', 'error');
      }
    } catch {
      showSnack('Error cancelling proxy', 'error');
    } finally {
      setCancelling(false);
    }
  };

  // ── Derived data ──────────────────────────────────────────────────────────
  const openSlots    = filteredVacancies.filter(s => !(s.proxyAssigned ?? s.ProxyAssigned));
const coveredSlots = filteredVacancies.filter(s =>   s.proxyAssigned ?? s.ProxyAssigned);

  // Within open slots, split by isUnavailablefor section labelling
  const absentSlots      = openSlots.filter(s =>  (s.isUnavailable?? s.isUnavailable?? true));
  const rescheduledSlots = openSlots.filter(s => !(s.isUnavailable?? s.isUnavailable?? true));

  const filteredHistory = useMemo(() =>
    history.filter(r =>
      !histSearch ||
      (r.absentTeacherName ?? '').toLowerCase().includes(histSearch.toLowerCase()) ||
      (r.proxyTeacherName  ?? '').toLowerCase().includes(histSearch.toLowerCase()) ||
      (r.subjectName       ?? '').toLowerCase().includes(histSearch.toLowerCase()) ||
      (r.className         ?? '').toLowerCase().includes(histSearch.toLowerCase())
    ), [history, histSearch]);

  const pagedHistory = useMemo(() =>
    filteredHistory.slice((histPage - 1) * histRows, histPage * histRows),
    [filteredHistory, histPage, histRows]);

  const filteredDuties = useMemo(() =>
    duties.filter(r =>
      !dutySearch ||
      (r.subjectName       ?? '').toLowerCase().includes(dutySearch.toLowerCase()) ||
      (r.className         ?? '').toLowerCase().includes(dutySearch.toLowerCase()) ||
      (r.absentTeacherName ?? '').toLowerCase().includes(dutySearch.toLowerCase())
    ), [duties, dutySearch]);

  const pagedDuties = useMemo(() =>
    filteredDuties.slice((dutyPage - 1) * dutyRows, dutyPage * dutyRows),
    [filteredDuties, dutyPage, dutyRows]);

  const filteredSugg  = suggestions.filter(s =>
  !suggSearch || (s.teacherName ?? s.TeacherName ?? '').toLowerCase().includes(suggSearch.toLowerCase())
);
const preferredSugg = filteredSugg.filter(s => (s.availabilityTag ?? s.AvailabilityTag) === 'Preferred');
const availSugg     = filteredSugg.filter(s => (s.availabilityTag ?? s.AvailabilityTag) !== 'Preferred');

  // Slot context for dialog — whether it's an absent or rescheduled case
  const assignSlotIsUnavailable = assignSlot
    ? (assignSlot.isUnavailable?? assignSlot.isUnavailable?? true)
    : true;

  // ── Tab style ─────────────────────────────────────────────────────────────
  const tabSx = {
    borderBottom: `1px solid ${T.border}`,
    px: 2, pt: 0.5, bgcolor: T.ghost,
    '& .MuiTab-root': {
      textTransform: 'none', fontWeight: 600, minHeight: 52, fontSize: '0.84rem',
      color: T.muted, gap: 0.8,
      '&.Mui-selected': { color: T.indigo, fontWeight: 700 },
    },
    '& .MuiTabs-indicator': { bgcolor: T.indigo, height: 3, borderRadius: '3px 3px 0 0' },
  };

  // ── Section label component (reusable for vacancy groups) ─────────────────
  const SectionLabel = ({ color, label, count, countBg, countColor, countBorder }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 2 }}>
      <Box sx={{ width: 4, height: 22, borderRadius: '4px', bgcolor: color }} />
      <Typography sx={{ fontWeight: 700, color: T.ink, fontSize: '0.95rem' }}>{label}</Typography>
      <Box sx={{
        bgcolor: countBg, color: countColor,
        fontWeight: 700, fontSize: '0.7rem',
        px: 1, py: 0.2, borderRadius: '20px',
        border: `1px solid ${countBorder}`,
      }}>
        {count}
      </Box>
    </Box>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ bgcolor: T.surface, minHeight: '100vh', pb: 6 }}>

      {/* ── Header ── */}
      <Box sx={{
        background: `linear-gradient(135deg, ${T.ink} 0%, #1A3460 60%, #1e4d8c 100%)`,
        pt: 4, pb: 3, px: { xs: 2, md: 4 },
        mb: 3,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""', position: 'absolute', top: -60, right: -60,
          width: 240, height: 240, borderRadius: '50%',
          background: 'rgba(37,99,235,0.15)',
        },
        '&::after': {
          content: '""', position: 'absolute', bottom: -40, left: '30%',
          width: 160, height: 160, borderRadius: '50%',
          background: 'rgba(37,99,235,0.08)',
        },
      }}>
        <Box sx={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'flex-start', flexWrap: 'wrap', gap: 2,
          position: 'relative', zIndex: 1,
        }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 0.5 }}>
              <Box sx={{ bgcolor: T.indigo, borderRadius: '10px', p: 0.9, display: 'flex' }}>
                <SwapIcon sx={{ color: '#fff', fontSize: 20 }} />
              </Box>
              <Typography variant="h5" sx={{ color: '#fff', fontWeight: 800, letterSpacing: -0.5, fontSize: '1.4rem' }}>
                Proxy Management
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', pl: 5.5, fontSize: '0.78rem' }}>
              Assign substitutes · Track history · View your duties
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>

            {/* Branch dropdown in header for master users */}
            {isMaster && branches.length > 0 && (
              <Box sx={{
                bgcolor: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '12px',
                px: 1.5, py: 0.5,
                display: 'flex', alignItems: 'center', gap: 1,
              }}>
                <LocationOnIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.6)' }} />
                <Select
                  value={selectedBranchId || ''}
                  onChange={e => setSelectedBranchId(Number(e.target.value))}
                  variant="standard"
                  disableUnderline
                  sx={{
                    color: '#fff', fontSize: '0.78rem', fontWeight: 600,
                    '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.6)', fontSize: 16 },
                    minWidth: 120,
                  }}
                  MenuProps={{ PaperProps: { sx: { borderRadius: '10px', mt: 1 } } }}
                >
                  {branches.map(b => (
                    <MenuItem key={b.branchId} value={b.branchId} sx={{ fontSize: '0.82rem' }}>
                      {b.branchName}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            )}

            {/* KPI chips */}
            {[
              { label: 'Absent',      value: absentSlots.length,      bg: 'rgba(217,119,6,0.15)',  border: 'rgba(251,191,36,0.3)',  color: '#FCD34D' },
              { label: 'Rescheduled', value: rescheduledSlots.length, bg: 'rgba(2,132,199,0.15)',  border: 'rgba(125,211,252,0.3)', color: '#7DD3FC' },
              { label: 'Covered',     value: coveredSlots.length,     bg: 'rgba(5,150,105,0.15)',  border: 'rgba(52,211,153,0.3)',  color: '#6EE7B7' },
              { label: 'Total',       value: vacancies.length,        bg: 'rgba(37,99,235,0.15)',  border: 'rgba(96,165,250,0.3)',  color: '#93C5FD' },
            ].map((k, i) => (
              <Box key={i} sx={{
                bgcolor: k.bg, border: `1px solid ${k.border}`,
                borderRadius: '12px', px: 2, py: 1,
                textAlign: 'center', minWidth: 64,
              }}>
                <Typography sx={{ fontWeight: 900, color: k.color, fontSize: '1.4rem', lineHeight: 1 }}>
                  {k.value}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.65rem', fontWeight: 600, mt: 0.2 }}>
                  {k.label}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>

      <Container maxWidth="xl" sx={{ px: { xs: 1.5, md: 3 } }}>
        <Card sx={{
          borderRadius: '16px',
          boxShadow: '0 4px 24px rgba(15,25,35,0.08)',
          border: `1px solid ${T.border}`,
          overflow: 'hidden',
        }}>

          {/* ── Tabs ── */}
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={tabSx}>
            <Tab icon={<TodayIcon sx={{ fontSize: 17 }} />}   label="Today's Vacancies" iconPosition="start" />
            <Tab icon={<HistoryIcon sx={{ fontSize: 17 }} />} label="Proxy History"     iconPosition="start" />
            <Tab icon={<DutyIcon sx={{ fontSize: 17 }} />}    label="My Duties"         iconPosition="start" />
          </Tabs>

          {/* ════════════════════════════════════════════════════════
              TAB 0 — TODAY'S VACANCIES
          ════════════════════════════════════════════════════════ */}
          {tab === 0 && (
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>

              {/* Filter row */}
              <Box sx={{
                display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap',
                bgcolor: T.ghost, p: 2, borderRadius: '12px', border: `1px solid ${T.border}`,
              }}>
                <TextField
                  label="Date" type="date" size="small" value={vacDate}
                  onChange={e => setVacDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 200, '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: T.card } }}
                />
                <Button variant="outlined" startIcon={<RefreshIcon sx={{ fontSize: 15 }} />}
                  onClick={loadVacancies}
                  sx={{ borderRadius: '10px', fontWeight: 600, borderColor: T.border, color: T.slate, '&:hover': { borderColor: T.indigo, color: T.indigo, bgcolor: '#EFF6FF' } }}>
                  Reload
                </Button>
                <FormControl size="small" sx={{ minWidth: 150 }}>
  <InputLabel sx={{ fontSize: '0.82rem' }}>Filter by Status</InputLabel>
  <Select
    value={vacFilter}
    label="Filter by Status"
    onChange={e => setVacFilter(e.target.value)}
    sx={{ borderRadius: '10px', fontSize: '0.83rem', bgcolor: T.card }}
  >
    <MenuItem value="all">All</MenuItem>
    <MenuItem value="absent">Absent</MenuItem>
    <MenuItem value="rescheduled">Rescheduled</MenuItem>
    <MenuItem value="covered">Covered</MenuItem>
  </Select>
</FormControl>
                {vacDate && vacDate !== todayStr() && (
                  <Button size="small" onClick={() => setVacDate(todayStr())}
                    sx={{ borderRadius: '8px', fontWeight: 600, color: T.indigo, bgcolor: '#EFF6FF', '&:hover': { bgcolor: '#DBEAFE' } }}>
                    ← Today
                  </Button>
                )}

                {/* Legend */}
                {vacancies.length > 0 && (
                  <Box sx={{
                    ml: 'auto', display: 'flex', alignItems: 'center', gap: 1.5,
                    flexWrap: 'wrap',
                  }}>
                    {[
                      { dot: T.rose,    label: 'Absent teacher'    },
                      { dot: T.sky,     label: 'Present (rescheduled)' },
                      { dot: T.emerald, label: 'Covered'           },
                    ].map((l, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: l.dot }} />
                        <Typography variant="caption" sx={{ color: T.muted, fontSize: '0.7rem' }}>{l.label}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              {loadingVac ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10, flexDirection: 'column', gap: 2 }}>
                  <CircularProgress sx={{ color: T.indigo }} />
                  <Typography variant="body2" color="text.secondary">Loading vacancies…</Typography>
                </Box>
              ) : vacancies.length === 0 ? (
                <Box sx={{ py: 8, textAlign: 'center' }}>
                  <Box sx={{ bgcolor: '#EFF6FF', borderRadius: '50%', width: 72, height: 72, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                    <CalIcon sx={{ fontSize: 36, color: T.indigo }} />
                  </Box>
                  <Typography sx={{ fontWeight: 700, color: T.ink, mb: 0.5 }}>No slots found</Typography>
                  <Typography variant="body2" color="text.secondary">No timetable slots found for this date. Try a different date.</Typography>
                </Box>
              ) : (
                <Box>

                  {/* ── Section 1: Absent teachers — isUnavailable= true ── */}
                  {absentSlots.length > 0 && (
                    <Box sx={{ mb: 4 }}>
                      <SectionLabel
                        color={T.rose}
                        label="Absent Teachers — Proxy Required"
                        count={absentSlots.length}
                        countBg="#FFE4E6"
                        countColor={T.rose}
                        countBorder="#FECDD3"
                      />
                      <Grid container spacing={2}>
                        {absentSlots.map((slot, i) => (
                          <Grid item xs={12} sm={6} md={4} lg={3}
                            key={slot.timetableId ?? slot.TimetableId ?? `absent-${i}`}>
                            <VacancyCard slot={slot} onAssign={handleOpenAssign} />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}

                  {/* ── Section 2: Present but rescheduled — isUnavailable= false ── */}
                  {rescheduledSlots.length > 0 && (
                    <Box sx={{ mb: 4 }}>
                      {/* Info banner */}
                      <Box sx={{
                        display: 'flex', alignItems: 'flex-start', gap: 1.2,
                        p: '10px 14px', mb: 2,
                        bgcolor: T.skyL,
                        border: `1px solid #BAE6FD`,
                        borderRadius: '10px',
                      }}>
                        <InfoIcon sx={{ fontSize: 15, color: T.sky, mt: 0.15, flexShrink: 0 }} />
                        <Typography variant="caption" sx={{ color: T.sky, fontSize: '0.74rem', lineHeight: 1.5 }}>
                          The teacher listed below is present today but is scheduled for another class during this period.
                          You may still assign a proxy to cover this slot.
                        </Typography>
                      </Box>

                      <SectionLabel
                        color={T.sky}
                        label="Present Teachers — Cover Needed for Another Slot"
                        count={rescheduledSlots.length}
                        countBg={T.skyL}
                        countColor={T.sky}
                        countBorder="#7DD3FC"
                      />
                      <Grid container spacing={2}>
                        {rescheduledSlots.map((slot, i) => (
                          <Grid item xs={12} sm={6} md={4} lg={3}
                            key={slot.timetableId ?? slot.TimetableId ?? `resch-${i}`}>
                            <VacancyCard slot={slot} onAssign={handleOpenAssign} />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}

                  {/* ── Section 3: Covered ── */}
                  {coveredSlots.length > 0 && (
                    <Box>
                      <SectionLabel
                        color={T.emerald}
                        label="Covered Slots"
                        count={coveredSlots.length}
                        countBg="#D1FAE5"
                        countColor={T.emerald}
                        countBorder="#6EE7B7"
                      />
                      <Grid container spacing={2}>
                        {coveredSlots.map((slot, i) => (
                          <Grid item xs={12} sm={6} md={4} lg={3}
                            key={slot.timetableId ?? slot.TimetableId ?? `covered-${i}`}>
                            <VacancyCard slot={slot} onAssign={handleOpenAssign} />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}

                </Box>
              )}
            </CardContent>
          )}

          {/* ════════════════════════════════════════════════════════
              TAB 1 — PROXY HISTORY
          ════════════════════════════════════════════════════════ */}
          {tab === 1 && (
            <>
              <CardContent sx={{ p: { xs: 2, md: 2.5 }, pb: '12px !important' }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={isMaster ? 2 : 3}>
                    <TextField label="From Date" type="date" size="small" fullWidth
                      value={histFrom} onChange={e => { setHistFrom(e.target.value); setHistPage(1); }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
                  </Grid>
                  <Grid item xs={12} sm={isMaster ? 2 : 3}>
                    <TextField label="To Date" type="date" size="small" fullWidth
                      value={histTo} onChange={e => { setHistTo(e.target.value); setHistPage(1); }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
                  </Grid>
                  <Grid item xs={12} sm={isMaster ? 3 : 4}>
                    <SearchBar value={histSearch} onChange={v => { setHistSearch(v); setHistPage(1); }}
                      placeholder="Search teacher, subject, class…" width="100%" />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Button variant="outlined" startIcon={<RefreshIcon sx={{ fontSize: 15 }} />}
                      onClick={loadHistory} fullWidth
                      sx={{ borderRadius: '10px', fontWeight: 600, borderColor: T.border, color: T.slate, '&:hover': { borderColor: T.indigo, color: T.indigo, bgcolor: '#EFF6FF' } }}>
                      Reload
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>

              <Divider />

              {loadingHist ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                  <CircularProgress sx={{ color: T.indigo }} />
                </Box>
              ) : (
                <>
                  <TableContainer sx={{ maxHeight: 460 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TH width={44}>#</TH>
                          <TH width={110}>Date</TH>
                          <TH>Absent Teacher</TH>
                          <TH>Proxy Teacher</TH>
                          <TH>Class / Subject</TH>
                          <TH>Period</TH>
                          <TH align="center" width={110}>Status</TH>
                          <TH>Remarks</TH>
                          <TH align="center" width={80}>Action</TH>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pagedHistory.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={9} align="center" sx={{ py: 7, color: T.muted }}>
                              {history.length === 0
                                ? 'No proxy records found. Try adjusting the date range.'
                                : 'No results match your search.'}
                            </TableCell>
                          </TableRow>
                        ) : pagedHistory.map((rec, idx) => {
                          const rowNum      = (histPage - 1) * histRows + idx + 1;
                          const status      = rec.proxyStatus ?? rec.ProxyStatus ?? '';
                          const isCancelled = status === 'Cancelled';
                          return (
                            <TableRow key={rec.id ?? rec.Id ?? idx} hover
                              sx={{ '&:last-child td': { border: 0 }, '&:hover': { bgcolor: '#F8FAFF' } }}>
                              <TableCell sx={{ color: T.faint, fontSize: '0.7rem' }}>{rowNum}</TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: T.ink, fontSize: '0.78rem' }}>
                                  {fmt(rec.proxyDate ?? rec.ProxyDate)}
                                </Typography>
                                <Typography variant="caption" sx={{ color: T.faint, fontSize: '0.64rem' }}>
                                  {rec.dayName ?? rec.DayName ?? ''}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Avatar sx={{ width: 28, height: 28, bgcolor: '#FFE4E6', color: T.rose, fontSize: '0.7rem', fontWeight: 800 }}>
                                    {(rec.absentTeacherName ?? rec.AbsentTeacherName ?? '?').charAt(0).toUpperCase()}
                                  </Avatar>
                                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                                    {rec.absentTeacherName ?? rec.AbsentTeacherName ?? '—'}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Avatar sx={{ width: 28, height: 28, bgcolor: '#D1FAE5', color: T.emerald, fontSize: '0.7rem', fontWeight: 800 }}>
                                    {(rec.proxyTeacherName ?? rec.ProxyTeacherName ?? '?').charAt(0).toUpperCase()}
                                  </Avatar>
                                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                                    {rec.proxyTeacherName ?? rec.ProxyTeacherName ?? '—'}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: T.ink, fontSize: '0.8rem' }}>
                                  {rec.subjectName ?? rec.SubjectName ?? '—'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: T.muted, fontSize: '0.68rem' }}>
                                  {rec.className ?? rec.ClassName ?? ''} {rec.sectionName ?? rec.SectionName ?? ''}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                  {rec.periodName ?? rec.PeriodName ?? '—'}
                                </Typography>
                                {(rec.startTime ?? rec.StartTime) && (
                                  <Typography variant="caption" sx={{ color: T.faint, fontSize: '0.64rem' }}>
                                    {rec.startTime ?? rec.StartTime}–{rec.endTime ?? rec.EndTime}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <StatusPill status={status} />
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" sx={{ color: T.muted, fontSize: '0.7rem' }}>
                                  {rec.remarks ?? rec.Remarks ?? '—'}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                {!isCancelled && (
                                  <Tooltip title="Cancel proxy">
                                    <IconButton size="small"
                                      onClick={() => { setCancelRec(rec); setCancelRemark(''); setCancelOpen(true); }}
                                      sx={{ color: T.rose, bgcolor: '#FFF1F2', width: 28, height: 28, '&:hover': { bgcolor: '#FFE4E6' } }}>
                                      <CancelIcon sx={{ fontSize: 15 }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Pager total={filteredHistory.length} page={histPage} rowsPerPage={histRows}
                    onPage={setHistPage} onRows={setHistRows} />
                </>
              )}
            </>
          )}

          {/* ════════════════════════════════════════════════════════
              TAB 2 — MY DUTIES
          ════════════════════════════════════════════════════════ */}
          {tab === 2 && (
            <>
              <CardContent sx={{ p: { xs: 2, md: 2.5 }, pb: '12px !important' }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={isMaster ? 3 : 4}>
                    <FormControl fullWidth size="small" disabled={teachers.length === 0}>
                      <InputLabel sx={{ fontSize: '0.82rem' }}>Teacher</InputLabel>
                      <Select
                        value={selectedTeacherId}
                        label="Teacher"
                        onChange={e => { setSelectedTeacherId(e.target.value); setDutyPage(1); }}
                        startAdornment={
                          <InputAdornment position="start">
                            <PersonIcon sx={{ fontSize: 14, color: T.faint, ml: 0.3 }} />
                          </InputAdornment>
                        }
                        sx={{
                          borderRadius: '10px', fontSize: '0.83rem',
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: T.border },
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: T.indigo },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: T.indigo },
                        }}
                        MenuProps={{ PaperProps: { sx: { borderRadius: '10px', mt: 0.5 } } }}
                      >
                        <MenuItem value="" sx={{ fontSize: '0.82rem', color: T.muted }}>
                          <em>All Teachers</em>
                        </MenuItem>
                        {teachers.map(t => (
                          <MenuItem
                            key={t.employeeId ?? t.EmployeeId}
                            value={t.employeeId ?? t.EmployeeId}
                            sx={{ fontSize: '0.82rem' }}
                          >
                            {t.employeeName ?? t.EmployeeName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={isMaster ? 2 : 3}>
                    <TextField label="Date" type="date" size="small" fullWidth
                      value={dutyFrom} onChange={e => { setDutyFrom(e.target.value); setDutyPage(1); }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
                  </Grid>
                  <Grid item xs={12} sm={isMaster ? 2 : 3}>
                    <SearchBar value={dutySearch} onChange={v => { setDutySearch(v); setDutyPage(1); }}
                      placeholder="Search subject, class…" width="100%" />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Button variant="outlined" startIcon={<RefreshIcon sx={{ fontSize: 15 }} />}
                      onClick={loadDuties} fullWidth
                      sx={{ borderRadius: '10px', fontWeight: 600, borderColor: T.border, color: T.slate, '&:hover': { borderColor: T.indigo, color: T.indigo, bgcolor: '#EFF6FF' } }}>
                      Reload
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>

              <Divider />

              {loadingDuty ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                  <CircularProgress sx={{ color: T.indigo }} />
                </Box>
              ) : (
                <>
                  <TableContainer sx={{ maxHeight: 460 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TH width={44}>#</TH>
                          <TH width={120}>Proxy Date</TH>
                          <TH>Absent Teacher</TH>
                          <TH>Class</TH>
                          <TH>Subject</TH>
                          <TH>Period</TH>
                          <TH align="center" width={110}>Status</TH>
                          <TH>Remarks</TH>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pagedDuties.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} align="center" sx={{ py: 7, color: T.muted }}>
                              {duties.length === 0
                                ? 'No proxy duties assigned from this date onwards.'
                                : 'No results match your search.'}
                            </TableCell>
                          </TableRow>
                        ) : pagedDuties.map((rec, idx) => {
                          const rowNum = (dutyPage - 1) * dutyRows + idx + 1;
                          return (
                            <TableRow key={rec.id ?? rec.Id ?? idx} hover
                              sx={{ '&:last-child td': { border: 0 }, '&:hover': { bgcolor: '#F8FAFF' } }}>
                              <TableCell sx={{ color: T.faint, fontSize: '0.7rem' }}>{rowNum}</TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: T.ink, fontSize: '0.78rem' }}>
                                  {fmt(rec.proxyDate ?? rec.ProxyDate)}
                                </Typography>
                                <Typography variant="caption" sx={{ color: T.faint, fontSize: '0.64rem' }}>
                                  {rec.dayName ?? rec.DayName ?? ''}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Avatar sx={{ width: 28, height: 28, bgcolor: '#FFE4E6', color: T.rose, fontSize: '0.7rem', fontWeight: 800 }}>
                                    {(rec.absentTeacherName ?? rec.AbsentTeacherName ?? '?').charAt(0).toUpperCase()}
                                  </Avatar>
                                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                                    {rec.absentTeacherName ?? rec.AbsentTeacherName ?? '—'}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                  {rec.className ?? rec.ClassName ?? '—'} {rec.sectionName ?? rec.SectionName ?? ''}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: T.ink, fontSize: '0.8rem' }}>
                                  {rec.subjectName ?? rec.SubjectName ?? '—'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                  {rec.periodName ?? rec.PeriodName ?? '—'}
                                </Typography>
                                {(rec.startTime ?? rec.StartTime) && (
                                  <Typography variant="caption" sx={{ color: T.faint, fontSize: '0.64rem' }}>
                                    {rec.startTime ?? rec.StartTime}–{rec.endTime ?? rec.EndTime}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <StatusPill status={rec.proxyStatus ?? rec.ProxyStatus} />
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" sx={{ color: T.muted, fontSize: '0.7rem' }}>
                                  {rec.remarks ?? rec.Remarks ?? '—'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Pager total={filteredDuties.length} page={dutyPage} rowsPerPage={dutyRows}
                    onPage={setDutyPage} onRows={setDutyRows} />
                </>
              )}
            </>
          )}

        </Card>
      </Container>

      {/* ════════════════════════════════════════════════════════════════
          ASSIGN PROXY DIALOG — context-aware for absent vs rescheduled
      ════════════════════════════════════════════════════════════════ */}
      <Dialog open={assignOpen} onClose={() => setAssignOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}>

        <DialogTitle sx={{
          background: assignSlotIsUnavailable
            ? `linear-gradient(135deg, ${T.ink} 0%, #1A3460 60%, #1e4d8c 100%)`
            : `linear-gradient(135deg, #0c3251 0%, #0369A1 60%, #0284C7 100%)`,
          color: '#fff', fontWeight: 700, py: 2.2, px: 3,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <Box sx={{
              bgcolor: assignSlotIsUnavailable ? T.indigo : T.sky,
              borderRadius: '8px', p: 0.6, display: 'flex',
            }}>
              <SwapIcon sx={{ fontSize: 18, color: '#fff' }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: '1rem' }}>Assign Proxy Teacher</Typography>
              <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.6)', mt: 0.1 }}>
                {assignSlotIsUnavailable
                  ? 'Teacher is absent — select a substitute'
                  : 'Teacher has another class — cover this slot'}
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={() => setAssignOpen(false)}
            sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.12)' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: '20px !important', pb: 0, px: 3 }}>

          {/* Slot summary card */}
          {assignSlot && (
            <Box sx={{
              p: 2, mb: 2.5, borderRadius: '12px',
              bgcolor: assignSlotIsUnavailable ? '#FFFBEB' : T.skyL,
              border: `1.5px solid ${assignSlotIsUnavailable ? '#FDE68A' : '#BAE6FD'}`,
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5,
            }}>
              {[
                {
                  label: 'Subject',
                  value: assignSlot.subjectName ?? assignSlot.SubjectName ?? '—',
                  icon: <ClassIcon sx={{ fontSize: 14, color: assignSlotIsUnavailable ? T.amber : T.sky }} />,
                },
                {
                  label: 'Class',
                  value: `${assignSlot.className ?? assignSlot.ClassName ?? ''} ${assignSlot.sectionName ?? assignSlot.SectionName ?? ''}`,
                  icon: null,
                },
                {
                  label: 'Period',
                  value: assignSlot.periodName ?? assignSlot.PeriodName ?? `P${assignSlot.periodId ?? assignSlot.PeriodId}`,
                  icon: <TimeIcon sx={{ fontSize: 14, color: assignSlotIsUnavailable ? T.amber : T.sky }} />,
                },
                {
                  label: assignSlotIsUnavailable ? 'Absent' : 'Teacher',
                  value: assignSlot.teacherName ?? assignSlot.TeacherName ?? '—',
                  icon: assignSlotIsUnavailable
                    ? <AbsentIcon sx={{ fontSize: 14, color: T.rose }} />
                    : <PersonOutlineIcon sx={{ fontSize: 14, color: T.sky }} />,
                },
              ].map((f, i) => (
                <Box key={i}>
                  <Typography variant="caption" sx={{
                    color: T.muted, fontWeight: 600, fontSize: '0.64rem',
                    textTransform: 'uppercase', letterSpacing: 0.5,
                    display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.2,
                  }}>
                    {f.icon}{f.label}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: T.ink, fontSize: '0.82rem' }}>
                    {f.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* Context note for rescheduled case */}
          {!assignSlotIsUnavailable && (
            <Box sx={{
              display: 'flex', alignItems: 'flex-start', gap: 1,
              p: '9px 12px', mb: 2,
              bgcolor: '#FFF7ED',
              border: `1px solid #FED7AA`,
              borderRadius: '8px',
            }}>
              <InfoIcon sx={{ fontSize: 14, color: T.amber, mt: 0.1, flexShrink: 0 }} />
              <Typography variant="caption" sx={{ color: '#92400E', fontSize: '0.73rem', lineHeight: 1.5 }}>
                This teacher is present today but is assigned to another class during this period.
                Assigning a proxy ensures this slot is covered.
              </Typography>
            </Box>
          )}

          {/* Proxy date */}
          <TextField
            label="Proxy Date" type="date" size="small" fullWidth
            value={proxyDate}
            onChange={e => setProxyDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
          />

          {/* Teacher list */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.2 }}>
  <Typography sx={{ fontWeight: 700, color: T.ink, fontSize: '0.85rem' }}>
    Select a Teacher
  </Typography>
  <SearchBar
    value={suggSearch}
    onChange={setSuggSearch}
    placeholder="Search teacher…"
    width={180}
  />
</Box>

          {loadingSugg ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 5, flexDirection: 'column', gap: 1.5 }}>
              <CircularProgress size={28} sx={{ color: T.indigo }} />
              <Typography variant="caption" color="text.secondary">Finding available teachers…</Typography>
            </Box>
          ) : suggestions.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center', bgcolor: T.ghost, borderRadius: '10px', border: `1px dashed ${T.border}` }}>
              <Typography variant="body2" color="text.secondary">No available teachers found for this slot.</Typography>
            </Box>
          ) : (
            <Box sx={{
              display: 'flex', flexDirection: 'column', gap: 0.8,
              maxHeight: 300, overflowY: 'auto', pr: 0.5,
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
              '&::-webkit-scrollbar-thumb': { bgcolor: T.border, borderRadius: 4 },
            }}>
              {preferredSugg.length > 0 && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, py: 0.5 }}>
                    <StarIcon sx={{ fontSize: 12, color: T.violet }} />
                    <Typography variant="caption" sx={{ fontWeight: 700, color: T.violet, fontSize: '0.64rem', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                      Subject Matched — Preferred
                    </Typography>
                  </Box>
                  {preferredSugg.map(t => {
                    const tid = t.teacherId ?? t.TeacherId;
                    return (
                      <TeacherSuggRow
                        key={tid}
                        teacher={t}
                        selected={pickedId === tid}
                        onSelect={() => handleSelectTeacher(t)}
                      />
                    );
                  })}
                  {availSugg.length > 0 && <Divider sx={{ my: 0.5 }} />}
                </>
              )}

              {availSugg.length > 0 && (
                <>
                  {preferredSugg.length > 0 && (
                    <Typography variant="caption" sx={{ fontWeight: 700, color: T.muted, fontSize: '0.64rem', textTransform: 'uppercase', letterSpacing: 0.6, py: 0.3 }}>
                      Other Available
                    </Typography>
                  )}
                  {availSugg.map(t => {
                    const tid = t.teacherId ?? t.TeacherId;
                    return (
                      <TeacherSuggRow
                        key={tid}
                        teacher={t}
                        selected={pickedId === tid}
                        onSelect={() => handleSelectTeacher(t)}
                      />
                    );
                  })}
                </>
              )}
            </Box>
          )}

          {/* Selected teacher indicator */}
          {pickedTeacher && (
            <Box sx={{ mt: 1.5, p: 1.2, borderRadius: '8px', bgcolor: '#EFF6FF', border: '1px solid #BFDBFE', display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon sx={{ fontSize: 14, color: T.indigo }} />
              <Typography variant="caption" sx={{ fontWeight: 700, color: T.indigo, fontSize: '0.75rem' }}>
                Selected: {pickedTeacher.teacherName ?? pickedTeacher.TeacherName}
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{
          p: '16px 24px', gap: 1.5, bgcolor: T.ghost,
          borderTop: `1px solid ${T.border}`,
          display: 'flex', flexDirection: 'column',
        }}>
          <TextField
            label="Remarks (optional)"
            size="small"
            fullWidth
            multiline
            rows={2}
            value={remarks}
            onChange={e => setRemarks(e.target.value)}
            placeholder={assignSlotIsUnavailable
              ? 'e.g. Absent due to medical leave'
              : 'e.g. Teacher engaged in another class'}
            sx={{
              mb: 1.5,
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                '&:hover fieldset': { borderColor: T.slate },
                '&.Mui-focused fieldset': { borderColor: T.indigo, borderWidth: '1.5px' },
              },
              '& .MuiInputLabel-root': { color: T.slate, fontWeight: 600, fontSize: '0.8rem' },
            }}
          />
          <Box sx={{ display: 'flex', gap: 1.5, width: '100%' }}>
            <Button
              onClick={() => setAssignOpen(false)}
              variant="outlined"
              sx={{
                flex: 1, borderRadius: '10px', fontWeight: 600,
                borderColor: T.border, color: T.slate,
                '&:hover': { borderColor: T.slate, bgcolor: 'rgba(0,0,0,0.04)' },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveProxy}
              variant="contained"
              disabled={savingProxy || !pickedTeacher}
              startIcon={savingProxy ? <CircularProgress size={14} color="inherit" /> : <CheckIcon />}
              sx={{
                flex: 1, borderRadius: '10px', fontWeight: 700,
                bgcolor: assignSlotIsUnavailable ? T.indigo : T.sky,
                boxShadow: assignSlotIsUnavailable
                  ? '0 2px 8px rgba(37,99,235,0.3)'
                  : '0 2px 8px rgba(2,132,199,0.25)',
                '&:hover': { bgcolor: assignSlotIsUnavailable ? T.indigoD : '#0369A1' },
                '&:disabled': { opacity: 0.5 },
              }}
            >
              {savingProxy ? 'Assigning…' : 'Assign Proxy'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* ── Cancel Dialog ── */}
      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}>
        <DialogTitle sx={{
          bgcolor: T.rose, color: '#fff', fontWeight: 700, py: 2.2, px: 3,
          display: 'flex', alignItems: 'center', gap: 1.2,
        }}>
          <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', borderRadius: '8px', p: 0.6, display: 'flex' }}>
            <WarnIcon sx={{ fontSize: 18 }} />
          </Box>
          Cancel Proxy Assignment
        </DialogTitle>
        <DialogContent sx={{ pt: '20px !important', px: 3 }}>
          <Typography sx={{ mb: 2.5, color: T.muted, fontSize: '0.88rem' }}>
            Cancel the proxy assignment for{' '}
            <Box component="span" sx={{ fontWeight: 800, color: T.ink }}>
              {cancelRec?.proxyTeacherName ?? cancelRec?.ProxyTeacherName ?? 'this teacher'}
            </Box>?
          </Typography>
          <TextField
            label="Reason (optional)" size="small" fullWidth multiline rows={2}
            value={cancelRemark} onChange={e => setCancelRemark(e.target.value)}
            placeholder="e.g. Teacher rejoined"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
          />
        </DialogContent>
        <DialogActions sx={{ p: '16px 20px', gap: 1.5, bgcolor: T.ghost, borderTop: `1px solid ${T.border}` }}>
          <Button onClick={() => setCancelOpen(false)} variant="outlined"
            sx={{ borderRadius: '10px', fontWeight: 600, borderColor: T.border, color: T.slate }}>
            Go Back
          </Button>
          <Button onClick={handleCancelProxy} variant="contained"
            disabled={cancelling}
            startIcon={cancelling ? <CircularProgress size={14} color="inherit" /> : <CancelIcon />}
            sx={{ borderRadius: '10px', fontWeight: 700, bgcolor: T.rose, '&:hover': { bgcolor: '#BE123C' } }}>
            {cancelling ? 'Cancelling…' : 'Yes, Cancel'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar open={snack.open} autoHideDuration={4500}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setSnack(s => ({ ...s, open: false }))} severity={snack.sev}
          variant="filled"
          sx={{ width: '100%', fontWeight: 600, borderRadius: '10px', boxShadow: '0 4px 16px rgba(0,0,0,0.15)' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}